import React, { useCallback, useEffect, useState } from "react";
import { Button, Spin } from "antd";
import { useTranslation } from "react-i18next";

import GoogleLoginButton from "./GoogleLoginButton";
import TelegramLoginButton from "./TelegramLoginButton";
import {
  TelegramWidgetPayload,
  loginWithGoogle,
  loginWithTelegramWidget,
  saveSession,
} from "../../services/auth";
import { getReferralCode } from "../../utils/platform";
import LanguageSelector from "../../components/LanguageSelector/LanguageSelector";
import { isKredafiShell } from "../../utils/brand";
import KredafiLogo from "../../kredafi/KredafiLogo";
import "../../kredafi/kredafi.css";
import "./Auth.css";

/**
 * Экран входа на сайте и в установленном приложении.
 *
 * Telegram стоит первым осознанно: аккаунт с деньгами у большинства уже
 * привязан к Telegram, и вход тем же способом ведёт в тот же аккаунт.
 * Google — для тех, кому удобнее с ноутбука или у кого нет Telegram.
 */

interface Props {
  onAuthenticated: (result: { userId: number; needsTelegram: boolean }) => void;
  onBack?: () => void;
}

/** Коды ошибок сервера в понятные человеку фразы. */
const ERROR_KEYS: Record<string, string> = {
  invalid_signature: "Telegram did not confirm the login. Please try again.",
  signature_expired: "The login data expired. Please press the button again.",
  invalid_token: "Google did not confirm the login. Please try again.",
  audience_mismatch: "Google sign-in is misconfigured. We are already on it.",
  email_not_verified:
    "Google has not verified your email. Verify it and sign in again.",
  google_not_configured: "Google sign-in is temporarily unavailable.",
  jwks_unavailable: "Google is unavailable right now. Sign in with Telegram.",
  banned: "This account is blocked. Please contact support.",
};

/** Код ошибки сервера → фраза для человека. Вынесено из компонента, чтобы
 *  обработчики входа не пересоздавались на каждый рендер. */
function describeError(err: any, t: (key: string) => string): string {
  console.error("[Auth error]", err?.response?.data || err);
  const code = err?.response?.data?.error;
  if (code && ERROR_KEYS[code]) {
    return t(ERROR_KEYS[code]);
  }
  if (err?.response?.data?.message) {
    return err.response.data.message;
  }
  if (err?.response?.status === 429) {
    return t("Too many attempts. Please wait a minute.");
  }
  return t("Could not sign in. Please try again.");
}

const Login: React.FC<Props> = ({ onAuthenticated, onBack }) => {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isRTL = (i18n.language || "en").startsWith("ar");
  const kredafi = isKredafiShell();
  // Тема — та же, что человек выбрал на лендинге: на KredaFi всегда светлая тема лендинга
  const theme: "light" | "dark" = (() => {
    if (kredafi) return "light";
    try {
      return localStorage.getItem("wt_landing_theme") === "dark" ? "dark" : "light";
    } catch {
      return "light";
    }
  })();

  // Обработка возврата из SSO-шлюза (через URL-параметры или postMessage из всплывающего окна)
  useEffect(() => {
    // 1. Проверяем токены в query параметрах (при редиректе)
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get("auth_token");
    const refreshToken = params.get("refresh_token");
    const userId = params.get("user_id");

    if (authToken && refreshToken && userId) {
      saveSession({
        status: "ok",
        token: authToken,
        refresh_token: refreshToken,
        user_id: Number(userId),
        created: false,
        expires_in: 3600,
      });

      // Очищаем URL от токенов в истории браузера
      window.history.replaceState({}, document.title, window.location.pathname);

      onAuthenticated({
        userId: Number(userId),
        needsTelegram: false,
      });
      return;
    }

    // 2. Слушаем сообщение от всплывающего SSO-окна (popup)
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data &&
        event.data.type === "KREDAFI_SSO_AUTH" &&
        event.data.session
      ) {
        const session = event.data.session;
        if (session.token && session.user_id) {
          saveSession(session);
          onAuthenticated({
            userId: Number(session.user_id),
            needsTelegram: Boolean(session.needs_telegram),
          });
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onAuthenticated]);

  const handleTelegram = useCallback(
    async (payload: TelegramWidgetPayload) => {
      setBusy(true);
      setError(null);
      try {
        const session = await loginWithTelegramWidget(payload, getReferralCode());
        onAuthenticated({
          userId: session.user_id,
          needsTelegram: false,
        });
      } catch (err) {
        setError(describeError(err, t));
        setBusy(false);
      }
    },
    [onAuthenticated, t]
  );

  const handleGoogle = useCallback(
    async (credential: string) => {
      setBusy(true);
      setError(null);
      try {
        const session = await loginWithGoogle(credential, getReferralCode());
        onAuthenticated({
          userId: session.user_id,
          needsTelegram: Boolean(session.needs_telegram),
        });
      } catch (err) {
        setError(describeError(err, t));
        setBusy(false);
      }
    },
    [onAuthenticated, t]
  );

  return (
    <div className={`auth-screen ${theme === "dark" && !kredafi ? "auth-dark" : ""} ${kredafi ? "kf-login-screen" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
      {/* Top Bar with Language Selector */}
      <div className="auth-topbar">
        <LanguageSelector theme={theme} compact={true} />
      </div>

      <div className="auth-card">
        <div className="auth-brand">
          <KredafiLogo size={72} theme={theme} />
        </div>
        <h1 className="auth-title">{t("Sign in to KredaFi")}</h1>
        <p className="auth-subtitle">
          {t("Use your Telegram or Google account to sign in on this device")}
        </p>

        {busy ? (
          <div style={{ padding: "24px 0" }}>
            <Spin size="large" />
          </div>
        ) : (
          <div className="auth-buttons">
            {/* Telegram Web Login */}
            <div style={{ width: "100%", textAlign: "center" }}>
              <TelegramLoginButton onAuth={handleTelegram} />
              <div className="auth-section-hint">
                {t("Log in using your Telegram account to access your wallet and balance")}
              </div>
            </div>

            <div className="auth-divider">{t("or with Google")}</div>

            {/* Google Web Login */}
            <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <GoogleLoginButton onCredential={handleGoogle} />
            </div>

            {error && <div className="auth-error">{error}</div>}
          </div>
        )}

        <div className="auth-note">
          {t(
            "Signing in with Google creates an account without Telegram. To withdraw funds and receive notifications, link Telegram in settings."
          )}
        </div>

        {!kredafi && (
          <div className="auth-legal">
            <a href="/privacy.html" target="_blank" rel="noreferrer">
              {t("Privacy Policy")}
            </a>
            <span className="auth-legal-sep" aria-hidden="true">·</span>
            <a href="/terms.html" target="_blank" rel="noreferrer">
              {t("Terms of Service")}
            </a>
          </div>
        )}

        {onBack && (
          <div style={{ marginTop: 20, textAlign: "center" }}>
            <Button type="text" onClick={onBack} style={{ color: "#8b949e", fontSize: 13 }}>
              ← {t("Back")}
            </Button>
          </div>
        )}
      </div>

      {kredafi && (
        <div className="auth-page-legal">
          <a href="/privacy.html" target="_blank" rel="noreferrer">
            {t("Privacy Policy")}
          </a>
          <span className="auth-legal-sep" aria-hidden="true">·</span>
          <a href="/terms.html" target="_blank" rel="noreferrer">
            {t("Terms of Service")}
          </a>
        </div>
      )}
    </div>
  );
};

export default Login;
